import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  LabelList,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Text
} from 'recharts';
import { formatCurrency, formatPercent } from 'utils';
import { dummyData } from '../Utils';
import { TooltipCharges } from '../Tooltips';
import MorningstarCopyright from 'components/MorningstarCopyright/MorningstarCopyright';
import { makeStyles } from '@material-ui/core';
import { existingVsRecommendedIndex } from 'components/Settings/Palette/utils';
import { useTheme } from '@material-ui/styles';
import { radiusVertical } from 'utils/charts';

interface ExistingPoliciesProps {
  data: Array<PolicyChargeTypeChartData>;
}

interface PolicyChargeTypeChartData {
  type: string;
  total_excluding_adviser_cash_value?: number;
  total_excluding_adviser_percentage_value?: number;
  number?: string;
  portfolio_type?: string;
}

interface TickProps {
  x: number;
  y: number;
  offset: number;
  payload: any;
  width: number;
  number?: string;
}

const useStyles = makeStyles((theme: any) => {
  return {
    legendWrapper: {
      display: 'flex',
      gap: '2rem',
      justifyContent: 'center',
      marginTop: '3rem',
      marginBottom: '-3rem'
    },
    legendItem: {
      display: 'flex',
      gap: '5px',
      alignItems: 'center'
    },
    legendColor: {
      width: '14px',
      height: '14px',
      borderRadius: '50%'
    },
    legendColorExisting: {
      backgroundColor:
        theme.palette.baseColors.charts['comparisons'][
          existingVsRecommendedIndex('existing')
        ]
    },
    legendColorRecommended: {
      backgroundColor:
        theme.palette.baseColors.charts['comparisons'][
          existingVsRecommendedIndex('recommended')
        ]
    }
  };
});

const CustomizedAxisTick = ({ x, y, payload, number }: TickProps) => {
  if (payload && payload.value === 'gap') return null;
  const isOverall =
    payload && payload.value && payload.value.toLowerCase() === 'overall'
      ? true
      : false;
  return (
    <Text
      x={x}
      y={y}
      width={100}
      textAnchor="middle"
      verticalAnchor="start"
      style={{ fontWeight: isOverall ? '500' : 'inherit', fontSize: '0.75rem' }}
    >
      {isOverall ? payload.value : `${payload.value} (${number || '-'})`}
    </Text>
  );
};

const Tick = ({ payload: { value }, ...rest }: any) => (
  <text {...rest} className="bar-chart-tick" dy={12}>
    {value}%
  </text>
);

const ExistingPolicies = ({ data }: ExistingPoliciesProps) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  const isDataProvided = data && data.length > 0;
  const dataToRender = isDataProvided ? data : dummyData.policyCharges;
  const adjustToPercentage = dataToRender.map(
    (data: PolicyChargeTypeChartData) => ({
      ...data,
      total_excluding_adviser_percentage_value:
        (data.total_excluding_adviser_percentage_value || 0) * 100
    })
  );

  const showLegend = adjustToPercentage.some(item => 'portfolio_type' in item);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100% - 4.125rem)'
        }}
      >
        <ResponsiveContainer width="100%" height={450}>
          <BarChart
            data={adjustToPercentage}
            barSize={70}
            margin={{
              top: 50,
              right: 80,
              left: isDataProvided ? 60 : -30,
              bottom: 20
            }}
          >
            <YAxis
              type="number"
              axisLine={isDataProvided}
              tickLine={false}
              tick={<Tick />}
              allowDecimals={false}
              domain={[0, (val: any) => Math.ceil(val)]}
              tickFormatter={(val: string) => `${val}%`}
            />
            {isDataProvided && <Tooltip content={TooltipCharges} />}
            {showLegend && (
              <Legend
                // layout="vertical"
                // verticalAlign="top"
                // align="center"
                content={
                  <div className={classes.legendWrapper}>
                    <div className={classes.legendItem}>
                      <div
                        className={`${classes.legendColor} ${classes.legendColorExisting}`}
                      ></div>
                      Existing
                    </div>
                    <div className={classes.legendItem}>
                      <div
                        className={`${classes.legendColor} ${classes.legendColorRecommended}`}
                      ></div>
                      Recommended
                    </div>
                  </div>
                }
              />
            )}
            <XAxis
              dataKey="type"
              type="category"
              axisLine={isDataProvided}
              tickLine={false}
              interval={0}
              tick={(args: any) => {
                const policyData = adjustToPercentage[args.index];
                return (
                  <CustomizedAxisTick
                    {...args}
                    number={policyData && policyData.number}
                  />
                );
              }}
              width={100}
            />

            <Bar
              dataKey="total_excluding_adviser_percentage_value"
              fill="#D95045"
              unit="%"
              radius={radiusVertical}
            >
              {isDataProvided && (
                <LabelList
                  dataKey="total_excluding_adviser_percentage_value"
                  position="top"
                  content={(val: any) => {
                    if (val.type === 'gap') return;
                    const isOverall = val.type.toLowerCase() === 'overall';
                    return (
                      <text
                        style={{
                          fontSize: '13px',
                          fontWeight: isOverall ? 500 : 400
                        }}
                        x={val.x + val.width / 2}
                        y={
                          (val.value < 0
                            ? val.y + val.offset
                            : val.y - val.offset) - 16
                        }
                        textAnchor="middle"
                      >
                        <tspan>{formatPercent(val.value)}</tspan>
                      </text>
                    );
                  }}
                />
              )}

              {isDataProvided && (
                <LabelList
                  dataKey="total_excluding_adviser_cash_value"
                  content={(val: any) => {
                    if (val.type === 'gap') return;
                    const isOverall = val.type.toLowerCase() === 'overall';
                    return (
                      <text
                        style={{
                          fontSize: '13px',
                          fontWeight: isOverall ? 500 : 400
                        }}
                        x={val.x + val.width / 2}
                        y={
                          (val.value < 0
                            ? val.y + val.offset
                            : val.y - val.offset) - 3
                        }
                        textAnchor="middle"
                      >
                        <tspan>{formatCurrency(val.value)}</tspan>
                      </text>
                    );
                  }}
                />
              )}
              {adjustToPercentage.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.portfolio_type
                      ? theme.palette.baseColors.charts['comparisons'][
                          existingVsRecommendedIndex(entry.portfolio_type)
                        ]
                      : theme.palette.baseColors.charts['comparisons'][0]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', padding: '0 20px' }}>
        <div style={{ padding: '20px 0', textWrap: 'nowrap' }}>
          Chart view excludes ongoing adviser charges.
        </div>
        <MorningstarCopyright />
      </div>
    </>
  );
};

export default ExistingPolicies;
